import GiftIcon from '@assets/icons/GiftIcon'
import { useApps } from '@auth/useApps'
import AppPaths from '@globals/paths'
import theme, { colors } from '@globals/themes/defaultTheme'
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Paper from '@mui/material/Paper'
import { SxProps } from '@mui/material/styles'
import { AppPermission } from '@shared/permissions'
import { T } from '@transifex/react'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import ActivityButton from './ActivityButton'
import RecognizeBottomNavButton from './RecognizeBottomNavButton'
import { isAdminPath } from './utils'

const bottomNavProps: SxProps = {
  zIndex: theme.zIndex.drawer + 1,
  borderTopWidth: 1,
  borderTopColor: colors.grayExtraLight,
  borderTopStyle: 'solid',
  borderRadius: 0,
  justifyContent: 'space-between',
  padding: '8px 8px 16px 8px',
}

const menuButtonStyle: SxProps = {}

const selectedMenuButtonStyle: SxProps = {
  '& .MuiBottomNavigationAction-label': {
    color: colors.bluePrimary,
  },
  '& svg': {
    color: colors.bluePrimary,
  },
}

type BottomNavMobileProps = {
  isMobileDrawerOpen: boolean
  onMobileDrawerToggle: () => void
}

const BottomNavMobile: React.FC<BottomNavMobileProps> = ({
  isMobileDrawerOpen,
  onMobileDrawerToggle,
}) => {
  const navigate = useNavigate()
  const [hasPermission] = useApps()
  const { pathname } = useLocation()

  const showRecognizeButton = useMemo(
    () =>
      hasPermission('recognitionApp', AppPermission.RecognitionAppBrowse) ||
      hasPermission('recognitionApp', AppPermission.RecognitionAppUpgradeLicense),
    [hasPermission],
  )

  const showRewardsButton = useMemo(
    () =>
      hasPermission('rewardsApp', AppPermission.RewardsAppRedeem) &&
      hasPermission('pointsApp', AppPermission.PointsAppRedeemPoints),
    [hasPermission],
  )

  const showCelebrationButton = useMemo(
    () =>
      hasPermission('celebrationApp', AppPermission.CelebrationAppBrowse) ||
      hasPermission('cardApp', AppPermission.CardAppBrowse),
    [hasPermission],
  )

  const showNotificationsButton = useMemo(
    () => hasPermission('notificationApp', AppPermission.NotificationAppBrowseMessages),
    [hasPermission],
  )

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    if (newValue !== 'more') {
      navigate(newValue)
      if (isMobileDrawerOpen) onMobileDrawerToggle()
    } else {
      onMobileDrawerToggle()
    }
  }

  const secondMenuOption = useMemo(() => {
    if (showRewardsButton) {
      return (
        <BottomNavigationAction
          label={<T _str="Rewards" />}
          value={AppPaths.tenant.rewardsProviders()}
          icon={<GiftIcon />}
        />
      )
    }
    if (showCelebrationButton) {
      return (
        <BottomNavigationAction
          label={<T _str="Celebrate" />}
          value={AppPaths.tenant.celebrate()}
          icon={<CelebrationOutlinedIcon />}
        />
      )
    }
    return (
      <BottomNavigationAction
        label={<T _str="People" />}
        value={AppPaths.tenant.people()}
        icon={<GroupsOutlinedIcon />}
      />
    )
  }, [showRewardsButton, showCelebrationButton])

  // TODO SYN-5643: Uncomment this when the admin menu is ready
  // const menuValue = useMemo(() => {
  //   return isAdminPath(pathname) || pathname === AppPaths.tenant.adminMenu()
  //     ? AppPaths.tenant.adminMenu()
  //     : AppPaths.tenant.menu()
  // }, [pathname])

  const menuValue = useMemo(() => {
    return isAdminPath(pathname) ? 'more' : AppPaths.tenant.menu()
  }, [pathname])

  return (
    <Paper elevation={0} sx={bottomNavProps}>
      <BottomNavigation showLabels value={pathname} onChange={handleChange} sx={{ height: '100%' }}>
        <BottomNavigationAction
          disableRipple
          label={<T _str="Home" />}
          value={AppPaths.tenant.home()}
          icon={<HomeOutlinedIcon />}
        />
        {secondMenuOption}
        {showRecognizeButton && <RecognizeBottomNavButton />}
        {showNotificationsButton && (
          <ActivityButton hasNotificationBrowsePermission={showNotificationsButton} />
        )}
        <BottomNavigationAction
          label={<T _str="Menu" />}
          value={menuValue}
          sx={isMobileDrawerOpen ? selectedMenuButtonStyle : menuButtonStyle}
          icon={<MenuOutlinedIcon />}
        />
      </BottomNavigation>
    </Paper>
  )
}

export default BottomNavMobile
